import { action } from "typesafe-actions";
import { IBusinessPolicyCoverage } from "./businessPolicyCoverage.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { buildBinderRequest } from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// Client-side timeout (ms) enforcing the 30-second bound for lead-scoped binder
// fetch/save requests (Requirements 3.5, 3.6).
const LEAD_BINDER_REQUEST_TIMEOUT = 30000;

// Feature: lead-binder-business-tabs
// Module-scoped guard preventing a duplicate lead-scoped coverage save from being
// dispatched while an earlier save for the lead binder is still in flight
// (Requirement 3.8). The customer-policy save path is intentionally not gated so its
// existing behavior remains byte-for-byte unchanged (Requirement 4.2).
let isLeadCoverageSaveInProgress = false;

export const FETCH_BUSINESS_POLICY_COVERAG_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_COVERAG_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_COVERAGE_LIST_FAILED";

export const fetchBusinessPolicyCoverageListProgress = () =>
  action(FETCH_BUSINESS_POLICY_COVERAG_LIST_PROGRESS);
export const fetchBusinessPolicyCoverageListSuccess = (
  data: IBusinessPolicyCoverage[]
) => action(FETCH_BUSINESS_POLICY_COVERAGE_LIST_SUCCESS, { data });
export const fetchBusinessPolicyCoverageListFailed = () =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_LIST_FAILED);

export const fetchBusinessPolicyCoverageListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageListProgress());

      let finalUrl = `/policy/get-policy-business-coverage-summary?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyCoverage[] = res.data.data;
      dispatch(fetchBusinessPolicyCoverageListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of fetchBusinessPolicyCoverageListAsync. Fetches the coverage
// summary list keyed by lead_id (never customer_policy_id) via the centralized
// endpoint map, so the Coverage binder sub-tab reads its records by the current lead
// (Requirements 3.1, 3.4). On failure or >30s timeout it flips the shared list load
// state to Failed (which the sub-tab surfaces as an error + retry) and shows a message
// (Requirements 3.5). Unsaved broker input lives in the form component, not this slice,
// so it is retained on failure. Mirrors the leadAutoApplicationActions precedent.
export const fetchLeadBusinessPolicyCoverageListAsync =
  (
    leadId: string,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageListProgress());

      const { url } = buildBinderRequest(
        "coverage",
        { kind: "lead", leadId },
        "fetch"
      );

      let finalUrl = url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT,
      });
      const data: IBusinessPolicyCoverage[] = res.data.data;
      dispatch(fetchBusinessPolicyCoverageListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Coverage binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyCoverageAsync =
  (
    coverage: IBusinessPolicyCoverage,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyCoverage) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/edit-policy-business-coverage-summary`, {
        ...coverage,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Policy coverage saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of addBusinesPolicyCoverageAsync. Persists the coverage record
// keyed by lead_id and strips customer_policy_id/customer_id from the payload so a
// lead-scoped save never carries a customer_policy_id (Requirements 3.2, 3.4). After a
// successful save it re-fetches the coverage list by the same lead_id so the sub-tab
// reflects the persisted records (Requirements 3.3, 3.7). A module-scoped guard plus
// saveLoaderProgress/saveLoaderCompleted prevent a duplicate in-flight save
// (Requirement 3.8). Failures / >30s timeouts surface a message and retain the broker's
// entered form input (the form owns its values, so nothing is discarded here)
// (Requirement 3.6).
export const upsertLeadBusinessPolicyCoverageAsync =
  (
    coverage: IBusinessPolicyCoverage,
    leadId: string,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyCoverage) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (isLeadCoverageSaveInProgress) {
      return;
    }
    isLeadCoverageSaveInProgress = true;
    try {
      dispatch(saveLoaderProgress());

      const { url } = buildBinderRequest(
        "coverage",
        { kind: "lead", leadId },
        "save"
      );

      const { customer_policy_id, customer_id, ...rest } = coverage as any;
      const res = await api.post(
        url,
        {
          ...rest,
          lead_id: leadId,
        },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Policy coverage saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the coverage list by the same lead_id after a successful save
      // (Requirements 3.3, 3.7).
      dispatch(fetchLeadBusinessPolicyCoverageListAsync(leadId, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Coverage binder record could not be saved. Please retry.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      isLeadCoverageSaveInProgress = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_BUSINESS_POLICY_COVERAG_PROGRESS =
  "FETCH_BUSINESS_POLICY_COVERAG_PROGRESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_SUCCESS =
  "FETCH_BUSINESS_POLICY_COVERAGE_SUCCESS";
export const FETCH_BUSINESS_POLICY_COVERAGE_FAILED =
  "FETCH_BUSINESS_POLICY_COVERAGE_FAILED";

export const fetchBusinessPolicyCoverageProgress = () =>
  action(FETCH_BUSINESS_POLICY_COVERAG_PROGRESS);
export const fetchBusinessPolicyCoverageSuccess = (
  data: IBusinessPolicyCoverage
) => action(FETCH_BUSINESS_POLICY_COVERAGE_SUCCESS, { data });
export const fetchBusinessPolicyCoverageFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_COVERAGE_FAILED, { errorMessage });

export const fetchBusinessPolicyCoverageAsync =
  (
    customerPolicyId: number,
    coverageId: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageProgress());
      const res = await api.get(
        `/policy/get-policy-business-coverage-summary?customer_policy_id=${customerPolicyId}&column=coverage_number&value=${coverageId}`
      );
      const data: IBusinessPolicyCoverage[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyCoverageSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyCoverageFailed(
            "Oops! We couldn't find any records associated with your coverage number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of fetchBusinessPolicyCoverageAsync. Loads a single coverage
// record for the edit form keyed by lead_id (never customer_policy_id) via the
// centralized endpoint map (Requirements 3.1, 3.4). Failures / >30s timeouts flip the
// coverage load state to Failed so the sub-tab surfaces an error + retry
// (Requirement 3.5).
export const fetchLeadBusinessPolicyCoverageAsync =
  (
    leadId: string,
    coverageId: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyCoverageProgress());
      const { url } = buildBinderRequest(
        "coverage",
        { kind: "lead", leadId },
        "fetch"
      );
      const res = await api.get(
        `${url}&column=coverage_number&value=${coverageId}`,
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT }
      );
      const data: IBusinessPolicyCoverage[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyCoverageSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyCoverageFailed(
            "Oops! We couldn't find any records associated with your coverage number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyCoverageFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Coverage binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINES_POLICY_COVERAGE_STATE =
  "CLEAR_BUSINES_POLICY_COVERAGE_STATE";
export const CLEAR_BUSINES_POLICY_COVERAGE = "CLEAR_BUSINES_POLICY_COVERAGE";

export const clearBusinessPolicyCoverageState = () =>
  action(CLEAR_BUSINES_POLICY_COVERAGE_STATE);

export const clearBusinessPolicyCoverage = () =>
  action(CLEAR_BUSINES_POLICY_COVERAGE);
